/**
 * MistDiagnosticService — single translation layer between
 * compiler/runtime/analysis errors and all UI diagnostic consumers.
 *
 * This is the ONLY module that should produce MistDiagnostic objects
 * from raw pipeline outputs. All UI components consume diagnostics
 * through this service.
 */

import type { MistDiagnostic, MistDiagnosticResult, MistSourceRange } from './diagnosticTypes';
import { makeDiagnostic, aggregateResult } from './diagnosticTypes';
import { LEGACY_CODE_MAP, ALL_ERROR_CODES } from './diagnosticCodes';
import {
  validate as rawValidate,
  type ValidateOptions,
  type MistDiagnostic as LegacyDiagnostic,
  type DeployPreflightContext,
} from './mistLanguageService';
import { compileToPvi, getGraphWarnings } from './pviGraphService';
import type { PviCompileResult, PviGraphWithSpans } from './pviTypes';

/* ================================================================== */
/*  Legacy → New diagnostic conversion                                */
/* ================================================================== */

/** Map old DiagnosticCategory to new category. */
function mapLegacyCategory(cat: string, code: string): MistDiagnostic['category'] {
  const newCode = code ? (LEGACY_CODE_MAP[code] ?? code) : '';
  if (newCode.startsWith('MIST_P0')) return 'Syntax';
  if (newCode.startsWith('MIST_T1')) return 'Type';
  if (newCode.startsWith('MIST_C2')) return 'ContractStructure';
  if (newCode.startsWith('MIST_V3')) return 'ValueFlow';
  if (newCode.startsWith('MIST_D4')) return 'Determinism';
  if (newCode.startsWith('MIST_B5')) return 'Build';
  if (newCode.startsWith('MIST_DP')) return 'DeployPreflight';
  if (newCode.startsWith('MIST_S7')) return 'Simulation';
  if (newCode.startsWith('MIST_A8')) return 'Adversarial';
  if (newCode.startsWith('MIST_I9')) return 'Internal';

  // fallback from old category
  switch (cat) {
    case 'syntax': return 'Syntax';
    case 'compiler': return 'Type';
    case 'analysis': return 'ValueFlow';
    case 'deploy': return 'DeployPreflight';
    default: return 'Syntax';
  }
}

function mapLegacyStage(cat: string, code: string): MistDiagnostic['stage'] {
  const newCode = code ? (LEGACY_CODE_MAP[code] ?? code) : '';
  const def = ALL_ERROR_CODES[newCode];
  if (def) return def.stage;

  switch (cat) {
    case 'syntax': return 'Parse';
    case 'compiler': return 'TypeCheck';
    case 'analysis': return 'StaticAnalysis';
    case 'deploy': return 'DeployPreflight';
    default: return 'Unknown';
  }
}

function mapLegacySeverity(sev: string): MistDiagnostic['severity'] {
  switch (sev) {
    case 'error': return 'Error';
    case 'warning': return 'Warning';
    case 'info': case 'hint': return 'Info';
    default: return 'Error';
  }
}

/** Convert a single legacy MistDiagnostic to the new formal model. */
export function convertLegacyDiagnostic(d: LegacyDiagnostic, sourceFile?: string): MistDiagnostic {
  const legacyCode = d.code ?? '';
  const newCode = LEGACY_CODE_MAP[legacyCode] ?? legacyCode;
  const codeDef = ALL_ERROR_CODES[newCode];

  const severity = mapLegacySeverity(d.severity);
  const category = codeDef?.category ?? mapLegacyCategory(d.category, legacyCode);
  const stage = codeDef?.stage ?? mapLegacyStage(d.category, legacyCode);

  const range: MistSourceRange = {
    startLine: d.line,
    startColumn: d.column,
    endLine: d.endLine ?? d.line,
    endColumn: d.endColumn ?? d.column + 1,
  };

  // Extract related range for duplicate declarations
  let relatedRanges: MistDiagnostic['relatedRanges'];
  const dupMatch = d.message.match(/First declared on line (\d+)/);
  if (dupMatch) {
    relatedRanges = [{
      range: { startLine: parseInt(dupMatch[1], 10), startColumn: 1, endLine: parseInt(dupMatch[1], 10), endColumn: 100 },
      message: 'Original declaration',
    }];
  }

  return makeDiagnostic({
    code: newCode || 'MIST_I9001',
    category,
    stage,
    severity,
    message: d.message,
    sourceFile,
    range,
    relatedRanges,
    ...(codeDef ? {
      deployBlocking: codeDef.deployBlocking,
      simulationBlocking: codeDef.simulationBlocking,
      graphBlocking: codeDef.graphBlocking,
    } : {}),
  });
}

/* ================================================================== */
/*  Graph warning → MistDiagnostic                                   */
/* ================================================================== */

export function convertGraphWarning(
  w: { nodeId: number; message: string; code: string },
  graph: PviGraphWithSpans,
  sourceFile?: string,
): MistDiagnostic {
  const legacyCode = w.code;
  const newCode = LEGACY_CODE_MAP[legacyCode] ?? legacyCode;
  const codeDef = ALL_ERROR_CODES[newCode];

  // Try to get source span from the graph node
  const node = graph.nodes.find(n => n.id === w.nodeId);
  const range: MistSourceRange | undefined = node?.span
    ? {
        startLine: node.span.startLine,
        startColumn: node.span.startColumn,
        endLine: node.span.endLine,
        endColumn: node.span.endColumn,
      }
    : undefined;

  return makeDiagnostic({
    code: newCode || 'MIST_V3001',
    category: codeDef?.category ?? 'ValueFlow',
    stage: codeDef?.stage ?? 'StaticAnalysis',
    severity: codeDef?.defaultSeverity ?? 'Warning',
    message: w.message,
    sourceFile,
    range,
    ...(codeDef ? {
      deployBlocking: codeDef.deployBlocking,
      simulationBlocking: codeDef.simulationBlocking,
      graphBlocking: codeDef.graphBlocking,
    } : {}),
    metadata: { nodeId: w.nodeId },
  });
}

/* ================================================================== */
/*  PVI compile result errors → MistDiagnostic                       */
/* ================================================================== */

function convertPviErrors(pviResult: PviCompileResult, sourceFile?: string): MistDiagnostic[] {
  const out: MistDiagnostic[] = [];

  for (const errMsg of pviResult.errors) {
    out.push(makeDiagnostic({
      code: 'MIST_B5004',
      category: 'Build',
      stage: 'PviLowering',
      severity: 'Error',
      message: errMsg,
      sourceFile,
      graphBlocking: true,
    }));
  }

  // Graph-level warnings (from PVI analysis)
  if (pviResult.graph) {
    const graphWarnings = getGraphWarnings(pviResult.graph);
    for (const gw of graphWarnings) {
      out.push(convertGraphWarning(gw, pviResult.graph, sourceFile));
    }
  }

  return out;
}

/* ================================================================== */
/*  Public API                                                        */
/* ================================================================== */

export interface AnalyzeOptions {
  source: string;
  fileName: string;
  deployContext?: DeployPreflightContext;
}

/**
 * Run full Mist analysis pipeline and produce unified diagnostics.
 *
 * Pipeline:
 *   1. Lex + Parse + TypeCheck + StaticAnalysis (via validate())
 *   2. PVI compilation + graph analysis (via compileToPvi())
 *   3. Convert all results to MistDiagnostic[]
 *   4. Aggregate blocking flags
 */
export function analyze(options: AnalyzeOptions): {
  result: MistDiagnosticResult;
  pviResult: PviCompileResult;
} {
  const { source, fileName, deployContext } = options;
  const diagnostics: MistDiagnostic[] = [];

  // Step 1: Run legacy validation pipeline
  const legacyResult = rawValidate({
    source,
    fileName,
    deployContext,
  } as ValidateOptions);

  // Convert legacy diagnostics
  for (const ld of legacyResult.diagnostics) {
    diagnostics.push(convertLegacyDiagnostic(ld, fileName));
  }

  // Step 2: PVI compilation
  const pviResult = compileToPvi(source, fileName);

  // Step 3: Convert PVI errors/warnings
  const pviDiags = convertPviErrors(pviResult, fileName);
  diagnostics.push(...pviDiags);

  // Deduplicate: same code + same range
  const seen = new Set<string>();
  const deduped: MistDiagnostic[] = [];
  for (const d of diagnostics) {
    const key = `${d.code}:${d.range?.startLine ?? 0}:${d.range?.startColumn ?? 0}:${d.message}`;
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(d);
    }
  }

  return {
    result: aggregateResult(deduped),
    pviResult,
  };
}

/**
 * Run deploy preflight only.
 */
export function preflightDeploy(
  fileName: string,
  deployContext: DeployPreflightContext,
): MistDiagnosticResult {
  const legacyResult = rawValidate({
    source: '',
    fileName,
    deployContext,
  });

  const diagnostics = legacyResult.diagnostics.map(d => convertLegacyDiagnostic(d, fileName));
  return aggregateResult(diagnostics);
}

/* ================================================================== */
/*  Formatting helpers (for shared UI rendering)                      */
/* ================================================================== */

/**
 * Format a diagnostic for display: "[Stage] CODE message"
 */
export function formatDiagnosticLine(d: MistDiagnostic): string {
  return `[${d.stage}] ${d.code} ${d.message}`;
}

/**
 * Get summary counts from diagnostics.
 */
export function summarize(diagnostics: MistDiagnostic[]): {
  errors: number;
  warnings: number;
  info: number;
  total: number;
} {
  let errors = 0, warnings = 0, info = 0;
  for (const d of diagnostics) {
    if (d.severity === 'Error') errors++;
    else if (d.severity === 'Warning') warnings++;
    else info++;
  }
  return { errors, warnings, info, total: diagnostics.length };
}
